import AuthenticatedController from './AuthenticatedController';
import * as APIRoutes from '@mm/api-routes';
import {
  ExtendedMessengerAdModel,
  MessengerAdModel,
} from '../model/MessengerAdModel';
import { AdSyncModel } from '../model/AdModel';
import { RemoteAd } from '../model/AdSetModel';
import { buildQueryString } from '@mm/utils';
import { MessengerAdRemoteAdModel } from '../model/MessengerAdRemoteAdModel';

type AvailableAdsOptions = {
  botId?: number;
  accountId?: string;
  campaignId?: string;
  next?: string;
  limit?: number;
};

export class MessengerAdController extends AuthenticatedController {
  static async getAll(botId: number): Promise<MessengerAdModel[]> {
    const response = await super.get(APIRoutes.MESSENGER_ADS(botId));
    return response.messengerAds;
  }

  static async prepareReplacementPage(
    botId: number,
    entityId: number,
    pageId?: number,
  ) {
    const response = await super.post(
      APIRoutes.MESSENGER_AD_REPLACEMENT_PAGE(botId, entityId),
      { pageId },
    );
    return response.messengerAd;
  }

  static async availableAds(
    options: AvailableAdsOptions,
  ): Promise<{ data: AdSyncModel[]; next: string | null; error?: string }> {
    const { accountId, campaignId, limit, botId, next } = options;
    const queryString = buildQueryString({
      ad_account_id: accountId,
      ad_campaign_id: campaignId,
      bot_id: botId,
      next,
      limit,
    });
    return super.get(APIRoutes.FULL_ADS_SYNC(queryString));
  }

  static async getById(botId: number, adId: number) {
    const response = await super.get(
      `${APIRoutes.MESSENGER_ADS(botId)}/${adId}`,
    );
    return response.messengerAd;
  }

  static async postMessengerAd(
    botId: number,
    entity: Partial<ExtendedMessengerAdModel>,
  ) {
    const response = await super.post(APIRoutes.MESSENGER_ADS(botId), entity);
    return response.messengerAd;
  }

  static async putMessengerAd(
    botId: number,
    entity: Partial<ExtendedMessengerAdModel>,
  ) {
    const response = await super.put(
      `${APIRoutes.MESSENGER_ADS(botId)}/${entity.id}`,
      entity,
    );
    return response.messengerAd;
  }

  static async createAudience(botId: number, adId: number) {
    const response = await super.post(
      APIRoutes.MESSENGER_AD_CREATE_AUDIENCE(botId, adId),
    );
    return response.messengerAd;
  }

  static async destroyAudience(botId: number, adId: number) {
    const response = await super.post(
      APIRoutes.MESSENGER_AD_DESTROY_AUDIENCE(botId, adId),
    );
    return response.messengerAd;
  }

  static async changeAudienceName(
    botId: number,
    adId: number,
    audienceName: string,
  ) {
    const response = await super.put(
      APIRoutes.MESSENGER_AD_CHANGE_AUDIENCE_NAME(botId, adId),
      { audienceName },
    );
    return response.messengerAd;
  }

  static async facebookSyncLegacy(
    botId: number,
    adId: number,
    accountUserId: number,
    selectedAds: RemoteAd[],
  ) {
    const response = await super.post(
      `${APIRoutes.MESSENGER_ADS(botId)}/${adId}/facebook_sync_legacy`,
      { selected_ads: selectedAds, account_user_id: accountUserId },
    );
  }

  static async facebookSync(
    botId: number,
    adId: number,
    body: { ads?: MessengerAdRemoteAdModel[] },
  ) {
    return super.post(
      `${APIRoutes.MESSENGER_ADS(botId)}/${adId}/facebook_sync`,
      body,
    );
  }

  static async deleteMessengerAd(botId: number, adId: number) {
    return super.delete(`${APIRoutes.MESSENGER_ADS(botId)}/${adId}`);
  }
}
